import React from "react";
import { Link } from "react-router-dom";
import Translator, { __ } from "services/Translator";

export function getGameDisplayTitle(gameDetails, fallbackLabel = "Game Name") {
  const name = gameDetails?.game_name;
  if (!name) {
    return __(fallbackLabel);
  }
  const providerName =
    gameDetails?.provider?.name ||
    gameDetails?.provider_name ||
    gameDetails?.game_provider_name ||
    gameDetails?.providerName ||
    "";
  return providerName ? `${name} - ${providerName}` : name;
}

function HomeIcon() {
  return (
    <svg
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 495.398 495.398"
      width="20"
      height="20"
      aria-hidden="true"
    >
      <path d="M487.083,225.514l-75.08-75.08V63.704c0-15.682-12.708-28.391-28.413-28.391c-15.669,0-28.377,12.709-28.377,28.391 v29.941L299.31,37.74c-27.639-27.624-75.694-27.575-103.27,0.05L8.312,225.514c-11.082,11.104-11.082,29.071,0,40.158 c11.087,11.101,29.089,11.101,40.172,0l187.71-187.729c6.115-6.083,16.893-6.083,22.976-0.018l187.742,187.747 c5.567,5.551,12.825,8.312,20.081,8.312c7.271,0,14.541-2.764,20.091-8.312C498.17,254.586,498.17,236.619,487.083,225.514z" />
      <path d="M257.561,131.836c-5.454-5.451-14.285-5.451-19.723,0L72.712,296.913c-2.607,2.606-4.085,6.164-4.085,9.877v120.401 c0,28.253,22.908,51.16,51.16,51.16h81.754v-126.61h92.299v126.61h81.755c28.251,0,51.159-22.907,51.159-51.159V306.79 c0-3.713-1.465-7.271-4.085-9.877L257.561,131.836z" />
    </svg>
  );
}

function TheaterIcon() {
  return (
    <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g clipPath="url(#gameplay_theater_clip)">
        <path
          d="M3.7998 3.73633V13.7363H16.2998V3.73633H3.7998ZM4.9998 5.03638H14.9998V12.5364H4.9998V5.03638Z"
          fill="currentColor"
        />
        <path d="M0 12.5371V17.5371H5V16.3371H1.2V12.5371H0Z" fill="currentColor" />
        <path d="M18.8 12.5371V16.3371H15V17.5371H20V12.5371H18.8Z" fill="currentColor" />
        <path d="M15 0.0371094V1.23706H18.8V5.03711H20V0.0371094H15Z" fill="currentColor" />
        <path d="M0 0.0371094V5.03711H1.2V1.23706H5V0.0371094H0Z" fill="currentColor" />
      </g>
      <defs>
        <clipPath id="gameplay_theater_clip">
          <rect width="20" height="17.5" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function FullscreenIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g clipPath="url(#gameplay_fullscreen_clip)">
        <path
          d="M2.52 14.22L0 11.7V18H6.3L3.78 15.48L7.65 11.61L6.39 10.35L2.52 14.22ZM15.48 3.78L18 6.3V0H11.7L14.22 2.52L10.35 6.39L11.61 7.65L15.48 3.78ZM14.22 15.48L11.7 18H18V11.7L15.48 14.22L11.61 10.35L10.35 11.61L14.22 15.48ZM3.78 2.52L6.3 0H0V6.3L2.52 3.78L6.39 7.65L7.65 6.39L3.78 2.52Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="gameplay_fullscreen_clip">
          <rect width="18" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

export default function GamePlayToolbar({
  gameTitle,
  isAuthenticated,
  isFavorite,
  onToggleFavorite,
  theaterMode,
  onToggleTheaterMode,
  onToggleFullScreen,
  showTheaterMode = true,
  className = "",
}) {
  return (
    <div className={`game_play-toolbar ${className}`.trim()}>
      <div className="game_play-toolbar__start">
        <Link
          hreflang={Translator.getSelectedLanguage().code}
          className="game_play-toolbar__home"
          to="/games/all"
          aria-label={__("Home")}
        >
          <HomeIcon />
        </Link>
      </div>

      <div className="game_play-toolbar__center">
        {isAuthenticated ? (
          <button
            type="button"
            className="game_play-toolbar__favorite"
            onClick={onToggleFavorite}
            aria-label={__("Favorite")}
          >
            <i className={(isFavorite ? "fas" : "far") + " fa-heart"} aria-hidden="true" />
          </button>
        ) : null}
        <h1 className="game_play-toolbar__title">{gameTitle}</h1>
      </div>

      <div className="game_play-toolbar__end">
        {showTheaterMode ? (
          <button
            type="button"
            className={
              "game_play-toolbar__icon-btn" + (theaterMode ? " game_play-toolbar__icon-btn--active" : "")
            }
            onClick={onToggleTheaterMode}
            aria-label={__("Theater Mode")}
            aria-pressed={theaterMode}
            title={__("Theater Mode")}
          >
            <TheaterIcon />
          </button>
        ) : null}
        <button
          type="button"
          className="game_play-toolbar__icon-btn"
          onClick={onToggleFullScreen}
          aria-label={__("Full-Screen")}
          title={__("Full-Screen")}
        >
          <FullscreenIcon />
        </button>
      </div>
    </div>
  );
}
